import { CustomLightboxService } from './modules/stories/services/custom-lightbox.service';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild, inject, signal } from '@angular/core';
import { TranslationService } from './services/translation.service';
import { LanguageService } from './services/language.service';
import { Router, RouterOutlet } from '@angular/router';
import { ToastService } from './services/toast.service';
import { GenericLoginService } from './services/generic-login.service';
import { NotificationService } from './services/notification.service';
import { HeaderComponent } from './components/header/header.component';
import { CommonModule } from '@angular/common';
import { GenericLoginPageComponent } from './components/generic-login-page/generic-login-page.component';
import { CustomToastComponent } from './modules/shared/components/toast/custom-toast.component';
import { NotificationComponent } from './modules/shared/components/notification/notification.component';
import { FooterComponent } from './components/footer/footer.component';
import { LightboxComponent } from './modules/stories/components/lightbox/lightbox.component';
import { LanguageSelectionPageComponent } from './components/language-selection-page/language-selection-page.component';
import { environment } from '../environments/environment';
import { CrowdinOtaService } from './services/crowdin-ota/crowdin-ota.service';
import { TranslateModule } from '@ngx-translate/core';
import { CountrySelectionService } from './services/country-selection/country-selection.service';
import { CountrySelectorComponent } from './modules/shared/country-selector/country-selector.component';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [
        HeaderComponent,
        RouterOutlet,
        CommonModule,
        GenericLoginPageComponent,
        CustomToastComponent,
        NotificationComponent,
        FooterComponent,
        LightboxComponent,
        LanguageSelectionPageComponent,
        TranslateModule,
        CountrySelectorComponent,
    ],
    standalone: true,
})
export class AppComponent implements OnInit, OnDestroy {
    title = 'route';
    language = 'en-GB';
    translatedArray: any[] = [];
    showToast = false;
    toastMessage: any;
    toastType: any;
    position: any;
    leftPosition: any;
    noAnimation = false;
    featureFlags: any;
    @ViewChild('scrollElement') scrollElement: ElementRef | any;
    showLangPicker = false;
    showLoginForNonProdEnviorment = false;
    showLightBox: any;
    showNotification = false;

    private readonly translationService = inject(TranslationService);
    private readonly languageService = inject(LanguageService);
    private readonly router = inject(Router);
    private toastService = inject(ToastService);
    private genericLoginService = inject(GenericLoginService);
    private lightboxService = inject(CustomLightboxService);
    private readonly notificationService = inject(NotificationService);
    private readonly _crowdinOtaService = inject(CrowdinOtaService);
    private readonly _countrySelectionService = inject(CountrySelectionService);

    showCountrySelectionPanel = this._countrySelectionService.shouldShowPanel;
    showCookieOverlay = signal(true);
    private _overlayPollInterval: any;
    private _cookieOverlayTimeout: any;

    constructor() {
        const fullURL = window.location.href.split('/');
        const languageIdFromUrl = window.location.href.split('/')[3];

        this.genericLoginService.getGenericLoginObservable().subscribe((value) => {
            this.showLoginForNonProdEnviorment =
                value !== true && !environment.production ? true : false;
        });

        if (this.languageService.checkIsValideLanguage(fullURL[3])) {
            localStorage.setItem('language', fullURL[3]);
        }

        const selectedLang = this.languageService.getLanguage();
        this.language = selectedLang;
        if (
            (languageIdFromUrl && this.languageService.checkIsValideLanguage(languageIdFromUrl)) ||
            fullURL.includes('logout') ||
            fullURL.includes('login') ||
            fullURL.some((url: string) => url.includes('login')) ||
            fullURL.includes('verify') ||
            fullURL.includes('anonymize') ||
            fullURL.includes('stories')
        ) {
            if (selectedLang) {
                this.language = selectedLang;
            } else {
                this.language = environment.supported_language[0];
                localStorage.setItem('language', this.language);
            }
        } else if (fullURL[3] !== '') {
            this.router.navigateByUrl('/' + this.language + '/404');
        } else if (fullURL[3] === '') {
            this.router.navigateByUrl('/' + this.language + '/home');
        }
    }

    ngOnDestroy(): void {
        clearInterval(this._overlayPollInterval);
        clearTimeout(this._cookieOverlayTimeout);
    }
    // Add overlay until user interaction with cookie layer
    private _startCookieOverlayPoll(): void {
        this._overlayPollInterval = setInterval(() => {
            const interacted = localStorage.getItem('uc_user_interaction');
            const cookieBanner = document.querySelector('cmm-cookie-banner');
            const isCookieBannerLoaded =
                cookieBanner &&
                cookieBanner.shadowRoot &&
                cookieBanner.shadowRoot.querySelector('[role="dialog"]');

            if (interacted === 'true' || isCookieBannerLoaded) {
                this.showCookieOverlay.set(false);
                clearInterval(this._overlayPollInterval);
                clearTimeout(this._cookieOverlayTimeout);
            }
        }, 200);

        this._cookieOverlayTimeout = setTimeout(() => {
            this.showCookieOverlay.set(false);
            clearInterval(this._overlayPollInterval);
        }, 20000);
    }

    async ngOnInit() {
        this._startCookieOverlayPoll();
        await this.translationService.getCustomerPortalConfiguration();
        await this._crowdinOtaService.initializeLanguage();
        this.featureFlags = this.translationService.featureFlags;
        this.getToastData();
        const videoStatus = sessionStorage.getItem('videoplayed');
        this._countrySelectionService.markConfigReady();

        if (videoStatus === 'true') {
            this.noAnimation = true;
        }

        this.languageService.showLanguageSelectionPage$.subscribe((isShowLangPicker) => {
            this.showLangPicker = isShowLangPicker;
        });

        this.lightboxService.lightboxState$.subscribe((item) => {
            this.showLightBox = item.isOpen;
        });

        this.notificationService.notificationState$.subscribe((state) => {
            this.showNotification = state.isOpen;
        });

        this.translationService.getAllSupportedLanguages().subscribe((supportedLanguages: any) => {
            this.translationService.supportedLanguagesForDisplay = supportedLanguages;
        });
    }

    getToastData() {
        this.toastService.toastDetails.subscribe((status: any) => {
            this.showToast = status.isShown;
            this.toastMessage = status.message;
            this.toastType = status.toastType;
            this.position = status.position;
            this.leftPosition = status.leftPosition;
        });
    }

    scrollTop() {
        this.scrollElement.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
}
